// src/store/modules/workflow.js
import * as api from "@/api/workflowAPI";
import * as validators from "@/utils/validators";
import { generateUUID } from "@/utils/uuid";
import scanShellImg from "@/assets/images/scanShell.png";
import scanBoardImg from "@/assets/images/scanBoard.png";
import scanLeftTab from "@/assets/images/scanLeftTab.png";
import scanRightTab from "@/assets/images/scanRightTab.png";

const normalizeDeviceId = (value = "") => value.trim().toUpperCase();

// 初始UI状态的工厂函数，方便重置
const getInitialUiState = () => ({
  isLoading: false,
  isFailed: false,
  failType: null,
  isRetry: false,
  retryTime: 0,
  isUploadException: false,
  // 新增: 用于存储后端返回的具体错误信息
  backendErrorMessage: null,
  showMessage: null,
});

const state = () => ({
  workflowType: null, // 当前工作流类型: 'potting', 'welding', 'binding', 'finalCheck', 'innerSampling'
  isConfigLocked: false, // 配置是否锁定
  currentStep: 1, // 当前步骤号
  stepConfigs: [], // 当前工作流的步骤配置数组
  uiState: getInitialUiState(), // UI状态对象
  seriesId: null, // 新增：当前流程的唯一ID
  errorSolution: {
    inconsistent_shellAndBoard: {
      title: "外壳与板子不匹配",
      message:
        "检测到扫描的板子二维码与外壳二维码不一致。\n\n" +
        "请按以下步骤排查：\n" +
        "1. 确认当前灌胶的板子是否是属于当前外壳。\n" +
        "2. 重新扫描一次板子二维码，确保扫码正确。",
      notify: "错误！板子与外壳二维码不匹配，请核实后重试。",
      imageUrl: null,
    },
    consistentTabs: {
      title: "左右标签重复",
      message:
        "检测到左右两侧扫描了相同的标签条码。\n\n" +
        "请确认是否误用同一个标签进行了两次扫描，并使用正确的标签重试。",
      notify: "错误！左右标签一致，请使用不同标签重试。",
      imageUrl: null,
    },
    networkError: {
      title: "网络异常",
      message:
        "无法连接到服务器或保存失败。\n\n" +
        "请按以下步骤排查：\n" +
        "1. 检查设备的网络连接是否正常。\n" +
        "2. 尝试刷新页面或稍后重试。",
      notify: "错误！网络连接失败，请检查网络后重试。",
      imageUrl: null,
    },
    powerOff: {
      title: "设备未上电",
      message:
        "系统未检测到设备的在线信号。\n" +
        "请检查设备的电源线是否连接稳固，并确认电源已开启。",
      notify: "错误！未检测到设备上电，请检查电源。",
      imageUrl: null,
    },
    gyroBreakDown: {
      title: "设备硬件故障",
      message:
        "系统检测到设备陀螺仪模块异常，可能已损坏。\n" +
        "请勿继续执行焊接流程。将此设备标记为故障设备，并联系技术人员处理。",
      notify: "严重错误！设备陀螺仪故障，请终止操作！",
      imageUrl: null,
    },
    packLost_06: {
      title: "数据丢包",
      message:
        "设备上电后，系统在规定时间内未收到必要的检测数据包。\n" +
        "请2.5小时后重试。",
      notify: "错误！数据丢包，请重试或检查设备。",
      imageUrl: null,
    },
    scanFail_BoardCode: {
      title: "板子二维码扫描失败",
      message:
        "扫描的板子二维码格式不正确，无法识别。\n" +
        "请按以下步骤排查：\n" +
        "1. 确保输入法已切换到英文模式。\n" +
        "2. 检查二维码是否清晰、完整，对准二维码中心重新扫描。\n" +
        "3. 确认格式是否为16位十六进制字符。正确示例：009569000004963F",
      notify: "错误！板子二维码格式错误，请根据提示排查。",
      imageUrl: scanBoardImg,
    },
    scanFail_ShellCode: {
      title: "外壳二维码扫描失败",
      message:
        "扫描的外壳二维码格式不正确，无法识别。\n" +
        "请按以下步骤排查：\n" +
        "1. 确保输入法已切换到英文模式。\n" +
        "2. 检查二维码是否清晰、完整，对准二维码中心重新扫描。\n" +
        "3. 确认格式是否为16位十六进制字符。正确示例：009569000004963F",
      notify: "错误！外壳二维码格式错误，请根据提示排查。",
      imageUrl: scanShellImg,
    },
    scanFail_ShellOrBoardCode: {
      title: "外壳二维码或板子二维码扫描失败",
      message:
        "扫描的外壳二维码格式不正确，无法识别。\n" +
        "请按以下步骤排查：\n" +
        "1. 确保输入法已切换到英文模式。\n" +
        "2. 检查二维码是否清晰、完整，对准二维码中心重新扫描。\n" +
        "3. 确认格式是否为16位十六进制字符。正确示例：009569000004963F" +
        "扫描的板子二维码格式不正确，无法识别。\n" +
        "\n" +
        "扫描的板子二维码格式不正确，无法识别。\n" +
        "请按以下步骤排查：\n" +
        "1. 确保输入法已切换到英文模式。\n" +
        "2. 检查二维码是否清晰、完整，对准二维码中心重新扫描。\n" +
        "3. 确认格式是否为16位十六进制字符。正确示例：009569000004963F",
      notify: "错误！二维码格式错误，请根据提示排查。",
      imageUrl: null,
    },
    illegalDevice: {
      title: "非法设备",
      message:
        "此设备未在本次生产计划中，或本流程已被执行过。\n" +
        "请确认：\n" +
        "1. 是否拿错设备。\n" +
        "2. 查询该设备操作记录。",
      notify: "错误！计划外的设备或重复操作。",
      imageUrl: null,
    },
    scanFail_UHF: {
      title: "超高频标签扫描失败",
      message:
        "扫描的超高频标签条码格式不正确。\n" +
        "请按以下步骤排查：\n" +
        "1. 确保输入法已切换到英文模式。\n" +
        "2. 确认格式是否为24位字母或数字。正确示例：RE280F3362000F0001667EEF",
      notify: "错误！超高频标签格式错误，请根据提示排查。",
      imageUrl: null,
    },
    illegalUHF: {
      title: "非法超高频标签",
      message: "此超高频标签已被使用过,请更换一个未经使用的新标签重试。",
      notify: "错误！计划外的超高频标签或已被使用。",
      imageUrl: null,
    },
    scanFail_HF: {
      title: "高频标签扫描失败",
      message:
        "扫描的高频标签条码格式不正确。\n" +
        "请按以下步骤排查：\n" +
        "1. 确保输入法已切换到英文模式。\n" +
        "2. 确认格式是否为8组冒号分隔的十六进制数，示例：C0:41:3B:6F:E3:A7:1D:E0",
      notify: "错误！高频标签格式错误，请根据提示排查。",
      imageUrl: null,
    },
    illegalHF: {
      title: "非法高频标签",
      message: "此高频标签已被使用过,请更换一个未经使用的新标签重试。",
      notify: "错误！计划外的高频标签或已被使用。",
      imageUrl: null,
    },
    wrongTagType: {
      title: "标签类型错误",
      message:
        "扫描的标签类型与配置中选择的类型不符。\n" +
        "例如，您配置的是扫描“超高频标签”，但实际扫描了“高频标签”。请检查配置或使用正确的标签。",
      notify: "错误！扫描的标签类型与配置不符。",
      imageUrl: null,
    },
  },
  stepTip: {
    scanShellCode: {
      title: "操作提示：扫描外壳二维码",
      message:
        "1. 将扫码枪对准设备外壳背面的二维码。\n" +
        "2. 确保光线充足，二维码清晰可见。\n" +
        "3. 确保光标出现在输入框上，输入法切换为英文模式。\n" +
        "4. 听到“滴”声后，条码将自动填入输入框。",
      imageUrl: scanShellImg,
    },
    scanBoardCode: {
      title: "操作提示：扫描板子二维码",
      message:
        "1. 将扫码枪对准电路板上的二维码。\n" +
        "2. 确保光线充足，二维码清晰可见。\n" +
        "3. 确保光标出现在输入框上，输入法切换为英文模式\n。" +
        "4. 听到“滴”声后，条码将自动填入输入框。",
      imageUrl: scanBoardImg,
    },
    scanLeftTab: {
      title: "操作提示：扫描左侧标签",
      message:
        "1. 将扫码枪对准设备左侧粘贴区域的标签。\n" +
        "2. 确保光标出现在输入框上，输入法切换为英文模式\n。" +
        "3. 听到“滴”声后，条码将自动填入输入框。",
      imageUrl: scanLeftTab,
    },
    scanRightTab: {
      title: "操作提示：扫描右侧标签",
      message:
        "1. 将扫码枪对准设备右侧粘贴区域的标签。\n" +
        "2. 确保光标出现在输入框上，输入法切换为英文模式\n。" +
        "3. 听到“滴”声后，条码将自动填入输入框。",
      imageUrl: scanRightTab,
    },
  },
});

const mutations = {
  INITIALIZE_WORKFLOW(state, { type, configs, lockable = false }) {
    state.workflowType = type;
    state.stepConfigs = JSON.parse(JSON.stringify(configs));
    state.currentStep = 1;
    state.isConfigLocked = !lockable;
    state.uiState = getInitialUiState();
    state.seriesId = generateUUID();
  },
  RESET_WORKFLOW(state, { type, configs }) {
    state.workflowType = type;
    state.stepConfigs = JSON.parse(JSON.stringify(configs));
    state.currentStep = 1;
    state.uiState = getInitialUiState();
    state.seriesId = generateUUID();
  },
  RESET_WORKFLOW_PRESERVING_CONFIG(state, { type, configs }) {
    state.workflowType = type;
    const newConfigs = JSON.parse(JSON.stringify(configs));
    newConfigs.forEach((newConfig) => {
      const currentConfig = state.stepConfigs.find(
        (c) => c.label === newConfig.label
      );
      if (currentConfig && newConfig.optional) {
        newConfig.enabled = currentConfig.enabled;
      }
    });
    state.stepConfigs = newConfigs;
    state.currentStep = 1;
    state.uiState = getInitialUiState();
    state.seriesId = generateUUID();
  },
  UPDATE_STEP_CONFIG(state, { stepTitle, newConfig }) {
    const target = state.stepConfigs.find((s) => s.title === stepTitle);
    if (target) {
      Object.assign(target, newConfig);
    }
  },
  UPDATE_STEP_VALUE(state, { stepIndex, value }) {
    if (state.stepConfigs[stepIndex]) {
      state.stepConfigs[stepIndex].value = value.trim();
    }
  },
  NORMALIZE_DEVICE_ID(state, deviceId) {
    if (state.stepConfigs[0]) {
      state.stepConfigs[0].value = normalizeDeviceId(deviceId);
    }
  },
  SET_CURRENT_STEP(state, step) {
    state.currentStep = step;
  },
  SET_LOADING(state, isLoading) {
    state.uiState.isLoading = isLoading;
  },
  SET_FAILURE(state, { failType, isRetry = false, backendMessage = null }) {
    state.uiState.isFailed = true;
    state.uiState.failType = failType;
    state.uiState.isLoading = false;
    state.uiState.isRetry = isRetry;
    state.uiState.retryTime++;
    state.uiState.backendErrorMessage = backendMessage;
  },
  SHOW_MESSAGE(state, { failType, isRetry = false, backendMessage = null }) {
    state.uiState.showMessage = backendMessage;
  },
  RESET_UI_STATE(
    state,
    { isRetry = false, isLoading = false, retryTime = 0 } = {}
  ) {
    Object.assign(state.uiState, getInitialUiState(), {
      isLoading,
      isRetry,
      retryTime,
    });
  },
  LOCK_CONFIG(state) {
    state.isConfigLocked = true;
  },
  UNLOCK_CONFIG(state) {
    state.isConfigLocked = false;
  },
  SET_UPLOAD_EXCEPTION_STATUS(state, status) {
    state.uiState.isUploadException = status;
  },
};

const getters = {
  currentStepConfig: (state) => state.stepConfigs[state.currentStep - 1] || {},
  displayMessage: (state, getters) => {
    const config = getters.currentStepConfig;
    const failType = state.uiState.failType;
    if (state.uiState.isFailed) {
      if (state.uiState.backendErrorMessage) {
        return state.uiState.backendErrorMessage;
      }
      if (state.errorSolution[failType]) {
        return state.errorSolution[failType].notify;
      }
      return "发生未知错误";
    }

    if (state.uiState.isLoading) {
      return config.prompt || "正在处理...";
    }

    if (state.uiState.showMessage) {
      return state.uiState.showMessage;
    }
    return config.desc || "";
  },
  isLastEnabledStep: (state) => {
    const enabledSteps = state.stepConfigs.filter(
      (s) => s.enabled && s.enabled !== "disabled"
    );
    if (enabledSteps.length === 0) return true;
    // 最后一个启用的步骤是“完成”步骤之前的那个
    const lastInteractiveStep = enabledSteps[enabledSteps.length - 2];
    const currentConfig = state.stepConfigs[state.currentStep - 1];
    return currentConfig && currentConfig.title === lastInteractiveStep?.title;
  },
};

const actions = {
  initializeWorkflow({ commit }, payload) {
    commit("INITIALIZE_WORKFLOW", payload);
  },
  resetWorkflow({ commit }, payload) {
    if (payload.preserveConfig) {
      commit("RESET_WORKFLOW_PRESERVING_CONFIG", payload);
    } else {
      commit("RESET_WORKFLOW", payload);
    }
  },
  navigateToNextStep({ commit, state }) {
    let nextStep = state.currentStep + 1;
    while (nextStep <= state.stepConfigs.length) {
      const nextStepConfig = state.stepConfigs[nextStep - 1];
      if (nextStepConfig.enabled && nextStepConfig.enabled !== "disabled") {
        commit("RESET_UI_STATE");
        commit("SET_CURRENT_STEP", nextStep);
        return;
      }
      nextStep++;
    }
  },
  async handleCurrentStep({ commit, dispatch, state }) {
    commit("RESET_UI_STATE", {
      isLoading: true,
      retryTime: state.uiState.retryTime,
    });
    try {
      const type = state.workflowType;
      const step = state.currentStep;
      const actionName = `${type}_step${step}`;
      if (actions[`${actionName}`]) {
        await dispatch(actionName);
      } else {
        console.warn(
          `Action ${actionName} not found, proceeding to next step.`
        );
        dispatch("navigateToNextStep");
      }
    } catch (error) {
      console.error("Critical error in handleCurrentStep:", error);
      commit("SET_FAILURE", {
        failType: "networkError",
        isRetry: true,
        backendMessage: error.message,
      });
    } finally {
      if (state.uiState.isLoading) {
        commit("SET_LOADING", false);
      }
    }
  },
  buildPayload({ state }, stepConfig) {
    return {
      seriesId: state.seriesId,
      operation: state.stepConfigs[state.currentStep - 1].title,
      deviceId: state.stepConfigs[0].value,
      content: stepConfig.value,
    };
  },
  async potting_step1({ commit, getters, dispatch, state }) {
    const value = getters.currentStepConfig.value.trim();
    const { eui, isValid } = validators.validateShellDeviceEUI(value);
    if (!isValid) {
      return commit("SET_FAILURE", { failType: "scanFail_ShellCode" });
    }
    commit("NORMALIZE_DEVICE_ID", eui);
    const payload = {
      seriesId: state.seriesId,
      operation: "灌胶",
      deviceId: eui,
      content: eui,
    };
    const result = await api.pottingAPI.validateDeviceByShell(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    }
    dispatch("navigateToNextStep");
  },
  async potting_step2({ commit, getters, dispatch, state }) {
    const value = getters.currentStepConfig.value.trim();
    const shellEui = state.stepConfigs[0].value.trim().toUpperCase();
    const { eui: boardEui, isValid } = validators.validateBoardDeviceEUI(value);
    if (!isValid) {
      return commit("SET_FAILURE", { failType: "scanFail_BoardCode" });
    }
    if (boardEui !== shellEui) {
      return commit("SET_FAILURE", { failType: "inconsistent_shellAndBoard" });
    }
    const payload = {
      seriesId: state.seriesId,
      operation: "灌胶",
      deviceId: shellEui,
      content: value,
    };
    const result = await api.pottingAPI.validateDeviceByBoard(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    }
    await dispatch("saveFinalState");
  },
  async welding_step1({ commit, getters, dispatch, state }) {
    const value = getters.currentStepConfig.value.trim();
    const { eui, isValid } = validators.validateShellDeviceEUI(value);
    if (!isValid) {
      return commit("SET_FAILURE", { failType: "scanFail_ShellCode" });
    }
    commit("NORMALIZE_DEVICE_ID", eui);
    const payload = {
      seriesId: state.seriesId,
      operation: "超声波焊接",
      deviceId: eui,
      content: eui,
    };
    const result = await api.weldingAPI.validateDevice(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    }
    dispatch("navigateToNextStep");
    await dispatch("welding_step2");
  },
  async welding_step2({ commit, dispatch, state }) {
    commit("SET_LOADING", true);
    const eui = state.stepConfigs[0].value;
    const payload = {
      seriesId: state.seriesId,
      operation: "超声波焊接",
      deviceId: eui,
      content: null,
    };
    const result = await api.weldingAPI.checkUploadStatus(payload);
    if (!result.success) {
      const message = result.message || "";
      if (message.includes("陀螺仪")) {
        return commit("SET_FAILURE", {
          failType: "gyroBreakDown",
          backendMessage: message,
        });
      } else if (message.includes("丢包")) {
        return commit("SET_FAILURE", {
          failType: "packLost_06",
          backendMessage: message,
        });
      } else {
        return commit("SET_FAILURE", {
          failType: "powerOff",
          backendMessage: message,
        });
      }
    }
    await dispatch("saveFinalState");
  },
  async binding_step1({ commit, getters, dispatch, state }) {
    const value = getters.currentStepConfig.value.trim();
    const { eui, isValid } = validators.validateShellDeviceEUI(value);
    if (!isValid) {
      return commit("SET_FAILURE", { failType: "scanFail_ShellCode" });
    }
    commit("NORMALIZE_DEVICE_ID", eui);
    const payload = {
      seriesId: state.seriesId,
      operation: "标签绑定",
      deviceId: eui,
      content: eui,
    };
    const result = await api.bindingAPI.validateShellCode(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    }
    if (getters.isLastEnabledStep) {
      await dispatch("saveFinalState");
    } else {
      dispatch("navigateToNextStep");
    }
  },
  async binding_step2({ commit, getters, dispatch, state }) {
    const { value, enabled } = getters.currentStepConfig;
    const tagType = validators.getTagType(value);
    if (tagType === "") {
      let currentConfig = state.stepConfigs[state.currentStep - 1];
      let failType =
        currentConfig.enabled.toUpperCase() === "UHF"
          ? "scanFail_UHF"
          : "scanFail_HF";
      return commit("SET_FAILURE", { failType });
    }
    if (enabled.toUpperCase() !== tagType) {
      return commit("SET_FAILURE", { failType: "wrongTagType" });
    }
    const payload = {
      seriesId: state.seriesId,
      operation: "标签绑定",
      deviceId: state.stepConfigs[0].value,
      content: JSON.stringify({
        leftTabId: value,
        leftTabType: tagType.toUpperCase(),
      }),
    };
    const result = await api.bindingAPI.validateLeftTab(payload);
    if (!result.success) {
      let failType = tagType === "UHF" ? "illegalUHF" : "illegalHF";
      return commit("SET_FAILURE", {
        failType,
        backendMessage: result.message,
      });
    }
    if (getters.isLastEnabledStep) {
      await dispatch("saveFinalState");
    } else {
      dispatch("navigateToNextStep");
    }
  },
  async binding_step3({ state, commit, getters, dispatch }) {
    const { value, enabled } = getters.currentStepConfig;
    const tagType = validators.getTagType(value);
    if (tagType === "") {
      let currentConfig = state.stepConfigs[state.currentStep - 1];
      let failType =
        currentConfig.enabled.toUpperCase() === "UHF"
          ? "scanFail_UHF"
          : "scanFail_HF";
      return commit("SET_FAILURE", { failType });
    }
    if (enabled.toUpperCase() !== tagType) {
      return commit("SET_FAILURE", { failType: "wrongTagType" });
    }
    const leftTabValue = state.stepConfigs[1].value;
    if (value === leftTabValue) {
      return commit("SET_FAILURE", { failType: "consistentTabs" });
    }
    const payload = {
      seriesId: state.seriesId,
      operation: "标签绑定",
      deviceId: state.stepConfigs[0].value,
      content: JSON.stringify({
        rightTabId: value,
        rightTabType: tagType.toUpperCase(),
      }),
    };
    const result = await api.bindingAPI.validateRightTab(payload);
    if (!result.success) {
      let failType = tagType === "UHF" ? "illegalUHF" : "illegalHF";
      return commit("SET_FAILURE", {
        failType,
        backendMessage: result.message,
      });
    }
    if (getters.isLastEnabledStep) {
      await dispatch("saveFinalState");
    } else {
      dispatch("navigateToNextStep");
    }
  },
  async finalCheck_step1({ commit, getters, dispatch, state }) {
    const { value } = getters.currentStepConfig;
    const { eui, isValid } = validators.validateShellDeviceEUI(value);
    if (!isValid) {
      return commit("SET_FAILURE", { failType: "scanFail_ShellCode" });
    }
    commit("NORMALIZE_DEVICE_ID", eui);
    const payload = {
      seriesId: state.seriesId,
      operation: "最终检查",
      deviceId: eui,
      content: eui,
    };
    const result = await api.finalCheckAPI.validateDevice(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    }
    dispatch("navigateToNextStep");
    await dispatch("finalCheck_step2");
  },
  async finalCheck_step2({ commit, dispatch, state }) {
    commit("SET_LOADING", true);
    const eui = state.stepConfigs[0].value;
    const payload = {
      seriesId: state.seriesId,
      operation: "最终检查",
      deviceId: eui,
      content: null,
    };
    const result = await api.finalCheckAPI.finalCheck(payload);
    if (!result.success) {
      const message = result.message || "";
      if (message.includes("陀螺仪")) {
        return commit("SET_FAILURE", {
          failType: "gyroBreakDown",
          backendMessage: message,
        });
      } else if (message.includes("丢包")) {
        return commit("SET_FAILURE", {
          failType: "packLost_06",
          backendMessage: message,
        });
      } else {
        return commit("SET_FAILURE", {
          failType: "powerOff",
          backendMessage: message,
        });
      }
    }
    await dispatch("saveFinalState");
  },
  async innerSampling_step1({ commit, getters, dispatch, state }) {
    const { value } = getters.currentStepConfig;
    const { eui, isValid } = validators.validateShellDeviceEUI(value);
    if (!isValid) {
      return commit("SET_FAILURE", { failType: "scanFail_ShellCode" });
    }
    commit("NORMALIZE_DEVICE_ID", eui);
    const payload = {
      seriesId: state.seriesId,
      operation: "内部抽检",
      deviceId: eui,
      content: eui,
    };
    const result = await api.innerSamplingAPI.validateDevice(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    }
    if (getters.isLastEnabledStep) {
      await dispatch("saveFinalState");
    } else {
      dispatch("navigateToNextStep");
    }
  },
  async innerSampling_step2({ commit, getters, dispatch, state }) {
    const { value, enabled } = getters.currentStepConfig;
    const tagType = validators.getTagType(value);
    if (tagType === "") {
      let currentConfig = state.stepConfigs[state.currentStep - 1];
      let failType =
        currentConfig.enabled.toUpperCase() === "UHF"
          ? "scanFail_UHF"
          : "scanFail_HF";
      return commit("SET_FAILURE", { failType });
    }
    if (enabled.toUpperCase() !== tagType) {
      return commit("SET_FAILURE", { failType: "wrongTagType" });
    }
    const payload = {
      seriesId: state.seriesId,
      operation: "内部抽检",
      deviceId: state.stepConfigs[0].value,
      content: JSON.stringify({
        leftTabId: value,
        leftTabType: tagType.toUpperCase(),
      }),
    };
    const result = await api.innerSamplingAPI.validateLeftTab(payload);
    if (!result.success) {
      let failType = tagType === "UHF" ? "illegalUHF" : "illegalHF";
      return commit("SET_FAILURE", {
        failType,
        backendMessage: result.message,
      });
    }
    if (getters.isLastEnabledStep) {
      await dispatch("saveFinalState");
    } else {
      dispatch("navigateToNextStep");
    }
  },
  async innerSampling_step3({ state, commit, getters, dispatch }) {
    const { value, enabled } = getters.currentStepConfig;
    const tagType = validators.getTagType(value);
    if (tagType === "") {
      let currentConfig = state.stepConfigs[state.currentStep - 1];
      let failType =
        currentConfig.enabled.toUpperCase() === "UHF"
          ? "scanFail_UHF"
          : "scanFail_HF";
      return commit("SET_FAILURE", { failType });
    }
    if (enabled.toUpperCase() !== tagType) {
      return commit("SET_FAILURE", { failType: "wrongTagType" });
    }
    const leftTabValue = state.stepConfigs[1].value;
    if (value === leftTabValue) {
      return commit("SET_FAILURE", { failType: "consistentTabs" });
    }
    const payload = {
      seriesId: state.seriesId,
      operation: "内部抽检",
      deviceId: state.stepConfigs[0].value,
      content: JSON.stringify({
        rightTabId: value,
        rightTabType: tagType.toUpperCase(),
      }),
    };
    const result = await api.innerSamplingAPI.validateRightTab(payload);
    if (!result.success) {
      let failType = tagType === "UHF" ? "illegalUHF" : "illegalHF";
      return commit("SET_FAILURE", {
        failType,
        backendMessage: result.message,
      });
    }
    if (getters.isLastEnabledStep) {
      await dispatch("saveFinalState");
    } else {
      dispatch("navigateToNextStep");
    }
  },
  async checkStatus_step1({ commit, getters, dispatch, state }) {
    const { value } = getters.currentStepConfig;
    let realEui = undefined;
    const { eui: shellEui, isValid: isValidShell } =
      validators.validateShellDeviceEUI(value);
    if (isValidShell) realEui = shellEui;
    else {
      const { eui: boardEui, isValid: isValidBoard } =
        validators.validateBoardDeviceEUI(value);
      if (isValidBoard) realEui = boardEui;
    }
    if (!realEui) {
      return commit("SET_FAILURE", { failType: "scanFail_ShellOrBoardCode" });
    }
    const payload = {
      seriesId: state.seriesId,
      operation: "设备状态",
      deviceId: realEui,
      content: value,
    };
    const result = await api.checkStatusAPI.checkStatus(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "illegalDevice",
        backendMessage: result.message,
      });
    } else {
      return commit("SHOW_MESSAGE", {
        backendMessage: `设备${realEui}当前状态是\r\n${result.message}`,
      });
    }
  },
  async saveFinalState({ dispatch, state, commit }) {
    const apiMap = {
      potting: api.pottingAPI,
      welding: api.weldingAPI,
      binding: api.bindingAPI,
      finalCheck: api.finalCheckAPI,
      innerSampling: api.innerSamplingAPI,
    };
    const workflowType = state.workflowType;
    let content;
    if (workflowType === "binding") {
      const leftTabConfig = state.stepConfigs.find(
        (c) => c.label === "scanLeftTab"
      );
      const rightTabConfig = state.stepConfigs.find(
        (c) => c.label === "scanRightTab"
      );
      content = JSON.stringify({
        leftTabId:
          leftTabConfig?.enabled !== "disabled" ? leftTabConfig.value : null,
        leftTabType:
          leftTabConfig?.enabled !== "disabled"
            ? leftTabConfig.enabled.toUpperCase()
            : null,
        rightTabId:
          rightTabConfig?.enabled !== "disabled" ? rightTabConfig.value : null,
        rightTabType:
          rightTabConfig?.enabled !== "disabled"
            ? rightTabConfig.enabled.toUpperCase()
            : null,
      });
    } else {
      content = state.stepConfigs
        .filter((c) => c.value)
        .map((c) => c.value)
        .join(",");
    }
    const payload = {
      seriesId: state.seriesId,
      operation:
        workflowType === "potting"
          ? "灌胶"
          : workflowType === "welding"
          ? "超声波焊接"
          : workflowType === "binding"
          ? "标签绑定"
          : workflowType === "finalCheck"
          ? "最终检查"
          : "内部抽检",
      deviceId: state.stepConfigs[0].value,
      content: content,
    };
    const result = await apiMap[workflowType].saveState(payload);
    if (!result.success) {
      return commit("SET_FAILURE", {
        failType: "networkError",
        isRetry: true,
        backendMessage: result.message,
      });
    }
    dispatch("navigateToNextStep");
  },
  async uploadException({ commit, state }) {
    commit("SET_LOADING", true);
    const errorInfo = {
      seriesId: state.seriesId,
      operation: state.stepConfigs[state.currentStep - 1].operation,
      deviceId: state.stepConfigs[0].value,
      extraData: JSON.stringify({
        uiState: state.uiState,
        configs: state.stepConfigs,
        isConfigLocked: state.isConfigLocked,
        workflowType: state.workflowType,
      }),
    };
    try {
      const result = await api.reportExceptionAPI(errorInfo);
      if (result.success) {
        commit("SET_UPLOAD_EXCEPTION_STATUS", true);
      }
    } catch (error) {
      console.error("Exception report failed:", error);
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
